/**
 * Kontrakt filtrů katalogu — JEDINÉ místo, kde žije mapování
 * `query string ↔ stav filtrů ↔ parametry API`.
 *
 * Do řezu 12 bylo totéž mapování rozepsané čtyřikrát (výpis `/zajezdy`, panel
 * `FilterSidebar`, `parseFilterParams` v API, výloha `/hotely`) a každý řez do
 * nich přidával zvlášť. Přidat filtr znamenalo sáhnout na čtyři místa a nic
 * nespadlo, když se na jedno zapomnělo — filtr tiše degradoval na „žádný filtr".
 * Proto tady:
 *
 * - `TripFilterState` — typovaný stav, jeden jmenný prostor parametrů,
 * - `parseFilterSearchParams` — JEDINÝ parser (web i API),
 * - `toApiParams` — stav → dotaz na `/api/trips` (překlad virtuální délky),
 * - `toSearchParams` / `writeFilterSearchParams` — stav → veřejná URL,
 * - `clearFilterSearchParams` — „Zrušit vše" nad celým jmenným prostorem,
 * - `countActiveFilters` — JEDNA definice odznaku aktivních filtrů,
 * - `hasAnyFilter` — „je co zrušit" (širší než odznak, viz jeho docblock),
 * - `hasSoftFilters` — hranice mezi tvrdými a měkkými filtry.
 *
 * Pravidla, která tu existují právě jednou:
 * - legacy alias `?tripType=small-group` → `guided` (odkazy před řezem 05),
 * - virtuální délka `?duration=day|multi` (dlaždice na HP + hero hledač) se
 *   překládá na `durationMin/durationMax` v `resolveDurationBounds`,
 * - hodnota na kraji Kč slideru = NEPŘÍTOMNÝ parametr (horní kraj nesmí
 *   vyloučit zájezdy nad 300 000 Kč),
 * - neznámá hodnota se zahazuje (kontinent, kraj, styl, typ, délkový bucket) —
 *   sdílený odkaz se nesmí zvrhnout v prázdný katalog bez vysvětlení,
 * - výčet klíčů pro „Zrušit vše" = `TRIP_FILTER_PARAM_KEYS` (+ legacy).
 *
 * Úplnost hlídá `tripFiltersGuard.test.ts` (obdoba `routesGuard.test.ts`):
 * každý klíč stavu musí projít round-tripem a být zařazen do počítadla.
 */
import {
  CONTINENTS,
  DESTINATION_REGIONS,
  DIFFICULTY_LEVELS,
  HOTEL_FEATURES,
  HOTEL_MEAL_PLANS,
  TRIP_STYLES,
  TRIP_TYPES,
  type Continent,
  type DestinationRegion,
  type DifficultyLevel,
  type HotelFeature,
  type HotelMealPlanType,
  type TripStyle,
  type TripType,
} from "./types";
import { DURATION_BUCKET_BY_KEY, PRICE_SLIDER, type DurationBucketKey } from "./tripBuckets";
import { isIsoDate } from "./utils";

/**
 * Virtuální klíče délky — nejsou to buckety, ale zkratky ze dvou vnějších
 * afordancí (dlaždice „Jednodenní/Vícedenní" na HP a pole typu v hero hledači).
 * V URL žijí pod `?duration=`, do API jdou jako `durationMin/durationMax`.
 */
const VIRTUAL_DURATIONS = {
  day: { min: 0, max: 1 },
  multi: { min: 2, max: null },
} as const satisfies Record<string, { min: number; max: number | null }>;

export type VirtualDuration = keyof typeof VIRTUAL_DURATIONS;

function isVirtualDuration(value: string): value is VirtualDuration {
  return Object.prototype.hasOwnProperty.call(VIRTUAL_DURATIONS, value);
}

/**
 * Legacy alias jmenného prostoru `?tripType=`: odkazy před řezem 05 posílaly
 * UI hodnotu `small-group`, kanonická je od té doby enum hodnota API `guided`.
 */
const TRIP_TYPE_PARAM_ALIASES: Readonly<Record<string, TripType>> = {
  "small-group": "guided",
};

function normalizeTripTypeParam(value: string): string {
  // `hasOwnProperty` guard, ne holý index: `?tripType=constructor` / `__proto__` by jinak
  // vrátil zděděnou hodnotu z prototypu (a `??` ji nezachytí, není nullish). Dnes to
  // spolkne následný `keepKnown`, ale ten je jen o jedno volání vedle — stejná chyba,
  // jakou kolo 2 opravovalo u `?duration=`.
  return Object.prototype.hasOwnProperty.call(TRIP_TYPE_PARAM_ALIASES, value)
    ? TRIP_TYPE_PARAM_ALIASES[value]
    : value;
}

/**
 * Celý jmenný prostor filtrů katalogu, v pořadí, v jakém se skládá dotaz.
 * Sdílí ho výpis, `/map`, `/routes` i `/hotely` — a `clearFilterSearchParams`
 * z něj dělá výčet pro „Zrušit vše", takže nový filtr nejde zapomenout smazat.
 */
export const TRIP_FILTER_PARAM_KEYS = [
  "destination",
  "continent",
  "region",
  "tripType",
  "hotels",
  "tripStyle",
  "difficultyLevel",
  "priceMin",
  "priceMax",
  "durationMin",
  "durationMax",
  "duration",
  "mealPlan",
  "hotelStars",
  "hotelFeature",
  "dateFrom",
  "dateTo",
  "dateTolerance",
  "bestMonth",
  "hasCzechGuide",
  "isFeatured",
  "isLastMinute",
  "isForWomen",
  "guide",
  "search",
] as const;

export type TripFilterParamKey = (typeof TRIP_FILTER_PARAM_KEYS)[number];

/**
 * Tolerance hledaného termínu ve dnech (vize doladeni-katalogu E1). UZAVŘENÝ výčet,
 * ne `number`: hodnota chodí z URL, takže `?dateTolerance=365` by z „hledám v srpnu"
 * beze slova udělal celý katalog. Nabídka je zároveň jediným zdrojem pro obě
 * afordance (hero hledač i panel filtrů), takže se přepínače nemohou rozejít.
 */
export type DateTolerance = 0 | 1 | 3 | 7;

export const DATE_TOLERANCE_OPTIONS = [0, 1, 3, 7] as const satisfies readonly DateTolerance[];

/**
 * Výchozí ±3 dny (E1). MUSÍ být shodné v `emptyTripFilterState()` i v parseru — guard
 * „Zrušit vše" porovnává výsledek s prázdným stavem, takže rozejití obou míst ho shodí.
 */
export const DEFAULT_DATE_TOLERANCE: DateTolerance = 3;

function isDateTolerance(value: number): value is DateTolerance {
  return (DATE_TOLERANCE_OPTIONS as readonly number[]).includes(value);
}

/**
 * Parametry, které kontrakt zná JEN proto, aby po nich uklidil.
 *
 * `?budget=` (bucketový filtr ceny) zmizel z UI v řezu 05. `?month=` a `?holiday=` zanikly
 * v doladění vize struktura-webu (řez 2), kde se termín stal JEDINOU časovou dimenzí;
 * staré `?month=`/`?holiday=` web čte jako neznámé (F5.2, řez 02), takže se sem jako
 * filtr nedostanou. Zůstávají ve výčtu proto, aby je „Zrušit vše" a serializátor uklidily
 * i tehdy, když se do adresy dostanou jinudy (mapa, přímý konzument API, ručně upravená URL).
 */
const LEGACY_TRIP_FILTER_PARAM_KEYS = ["budget", "month", "holiday"] as const;

/** Typovaný stav filtrů. Klíče = parametry URL + `virtualDuration` (sdílí `?duration=`). */
export interface TripFilterState {
  /** Slugy destinací (země i lokality); roll-up na děti řeší API. */
  destination: string[];
  continent: Continent | null;
  region: DestinationRegion | null;
  /**
   * JEDNA hodnota, ne pole: `guided`/`private`/`resort` jsou tři různé produkty,
   * ne aditivní vlastnost — „poznávací i pobytový zároveň" si nikdo nekupuje
   * (vize doladeni-katalogu B2). Legacy `small-group` je už přepsaný na `guided`,
   * legacy URL s víc hodnotami degraduje na první známou.
   */
  tripType: TripType | null;
  /** `?hotels=true` — jen pobytové na vyžádání (výloha `/hotely`). */
  hotels: boolean;
  tripStyle: TripStyle[];
  difficultyLevel: DifficultyLevel[];
  /** Kč; `null` = kraj slideru / parametr chybí. */
  priceMin: number | null;
  priceMax: number | null;
  /** Dny; hero hledač a překlad virtuální délky. */
  durationMin: number | null;
  durationMax: number | null;
  /** Délkové buckety panelu (multi-select). Vzájemně výlučné s `virtualDuration`. */
  duration: DurationBucketKey[];
  /**
   * „Jednodenní výlety" / „Vícedenní zájezdy" (`?duration=day|multi`) — POUZE PRŮCHOZÍ.
   *
   * Uvnitř aplikace ji od doladění vize `struktura-webu` NIKDO nevyrábí: dlaždice
   * homepage se v řezu 04 překlopily na tři kanonické typy a hero roleta, poslední
   * zbylý vstup, nabízí od stejné úpravy taky jen typy. Zůstává, protože příchozí
   * adresa (sdílený odkaz, zpětný odkaz, starý záložkovaný výpis) ji dál nese —
   * parser ji přijme a `DurationSection` ji zákazníkovi vysvětlí informačním pruhem.
   *
   * Follow-up (`docs/follow-ups.md`): po rozhodnutí o zděděných jednodenkách (fáze 4)
   * posoudit, jestli pojem vůbec zůstává.
   */
  virtualDuration: VirtualDuration | null;
  /**
   * ── Tři dimenze POBYTOVÝCH zájezdů (`tripType=resort`) ──
   *
   * U pobytové dovolené se zákazník ptá na jiné věci než u poznávacího zájezdu: co je
   * v ceně jídla, jak je hotel dobrý a pro koho je (jen pro dospělé / s bazénem). Styl
   * cestování — taxonomie postavená pro poznávačky — na to odpovědět neumí, a proto ho
   * panel u pobytových nahrazuje právě těmito třemi.
   *
   * V kontraktu žijí bez ohledu na typ: URL může nést cokoli a filtr musí platit i tehdy,
   * když se na něj přijde odjinud než z panelu. Které sekce se VYKRESLÍ, rozhoduje panel.
   */
  mealPlan: HotelMealPlanType[];
  /** Hvězdičky hotelu (3/4/5); jednotlivé hodnoty, ne rozsah — zákazník klika „4 a 5". */
  hotelStars: number[];
  hotelFeature: HotelFeature[];
  /**
   * ISO `YYYY-MM-DD`, nebo prázdný řetězec = kraj nezadaný. Parser jinou hodnotu
   * NEPUSTÍ (`2026-8-1` ani `2026-13-45`) — API nad tímhle polem počítá toleranci
   * přes `shiftIsoDate`, kde by nesmyslné datum skončilo `RangeError`.
   */
  dateFrom: string;
  dateTo: string;
  /**
   * O kolik dní se zadané okno roztahuje na OBOU koncích (E3). Modifikátor dimenze
   * `dateFrom`/`dateTo`, která je od řezu 2 JEDINOU časovou dimenzí kontraktu —
   * svátek i měsíc jsou jen zkratky, které ji předvyplní (staré `?month=`/`?holiday=`
   * web čte jako neznámé, F5.2, řez 02), takže platí i pro ně.
   *
   * Od řezu 06 se netýká jen odletu: okno znamená „zájezd se do něj vejde CELÝ"
   * (odlet ≥ `dateFrom`, NÁVRAT ≤ `dateTo`), takže tolerance posouvá obě hranice —
   * začátek dřív a konec později. Zájezd se tedy kvalifikuje i dřívějším odletem,
   * i pozdějším návratem. Posunutý začátek nikdy nespadne před dnešek.
   */
  dateTolerance: DateTolerance;
  /**
   * Měsíc, ve kterém má destinace zájezdu NEJLEPŠÍ POČASÍ (1–12), nebo `null`.
   *
   * NENÍ to termín odletu — ten je `dateFrom`/`dateTo` a od řezu 2 je to jediná časová
   * dimenze kontraktu. Tahle dimenze se vyhodnocuje proti `weatherData.bestMonths`
   * destinací zájezdu (primární + tagy), a to VČETNĚ dědění po publikovaných předcích;
   * „dobré" měsíce (`goodMonths`) ji nezapínají. Domov predikátu je proto v API
   * u destinačních filtrů (`applyDestinationDerivedFilters`), ne u příznaků.
   *
   * Jméno `month` je pro tuhle dimenzi ZAKÁZANÉ: existovalo ve významu „měsíc odletu",
   * zaniklo v doladění vize struktura-webu a staré `?month=`/`?holiday=` web čte jako
   * neznámé (F5.2, řez 02; `LEGACY_TRIP_FILTER_PARAM_KEYS`). Dvě jména, dvě různá chování.
   */
  bestMonth: number | null;
  hasCzechGuide: boolean;
  isFeatured: boolean;
  isLastMinute: boolean;
  /**
   * Zájezd určený ženám (vize C11) — samostatný příznak zájezdu, ne styl a ne štítek.
   * TVRDÝ filtr, jako `isFeatured`: kdo si ho zapne, nesmí pod čarou dostat zájezd
   * bez něj. Obdoba pro muže je Ne-cílem vize a guard kontraktu ji zakazuje.
   */
  isForWomen: boolean;
  guide: string;
  search: string;
}

/** Prázdný stav — vždy čerstvá pole, aby ho volající mohl bezpečně mutovat. */
export function emptyTripFilterState(): TripFilterState {
  return {
    destination: [],
    continent: null,
    region: null,
    tripType: null,
    hotels: false,
    tripStyle: [],
    difficultyLevel: [],
    priceMin: null,
    priceMax: null,
    durationMin: null,
    durationMax: null,
    duration: [],
    virtualDuration: null,
    mealPlan: [],
    hotelStars: [],
    hotelFeature: [],
    dateFrom: "",
    dateTo: "",
    dateTolerance: DEFAULT_DATE_TOLERANCE,
    bestMonth: null,
    hasCzechGuide: false,
    isFeatured: false,
    isLastMinute: false,
    isForWomen: false,
    guide: "",
    search: "",
  };
}

// ── Parser ──────────────────────────────────────────────────────────────────

const unique = <T>(values: readonly T[]): T[] => [...new Set(values)];

const csvValues = (raw: string | null | undefined): string[] =>
  raw
    ? unique(
        raw
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      )
    : [];

const keepKnown = <T extends string>(known: readonly T[], values: readonly string[]): T[] =>
  values.filter((v): v is T => (known as readonly string[]).includes(v));

const pickKnown = <T extends string>(
  known: readonly T[],
  raw: string | null | undefined,
): T | null => (raw && (known as readonly string[]).includes(raw) ? (raw as T) : null);

/**
 * Kč hranice: prázdno/nečíslo = parametr chybí, jinak ořez do rozsahu slideru.
 * Hodnota na `edge` (kraji) znamená „bez omezení" — panel takovou pozici nikdy
 * neemituje a odznak filtrů ji nepočítá, takže ji ani parser nesmí považovat za
 * aktivní (dřív si to výpis a panel protiřečily).
 */
function parsePriceBound(raw: string | null | undefined, edge: number): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const clamped = Math.min(Math.max(Math.round(n), PRICE_SLIDER.min), PRICE_SLIDER.max);
  return clamped === edge ? null : clamped;
}

/**
 * Mez délky ve dnech. Nula zůstává platná — dlaždice „Jednodenní" na HP se překládá na
 * `durationMin=0`, takže ořez zdola na 1 by z ní vyloučil zážitky měřené v hodinách.
 */
const DAY_BOUND_MAX = 365;

/**
 * Meze délky z URL: TÝŽ režim jako u ostatních parserů kontraktu, tedy `Number()` a ořez.
 *
 * `Number.parseInt` bral jakýkoli číselný PREFIX (`?durationMin=15abc` → 15), zatímco cena,
 * datum i tolerance smetí odmítají — sdílený odkaz s upravenou hodnotou tak u jediné dimenze
 * zabíral místo aby se zahodil. Ořez shora navíc drží hodnotu v rozsahu, který má nad
 * katalogem smysl.
 */
function parseDayBound(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(Math.max(Math.round(n), 0), DAY_BOUND_MAX);
}

/**
 * Hvězdičky z URL: jen celá čísla 1–5, cokoli jiného se zahodí. Bez ořezu by
 * `?hotelStars=0` znamenalo filtr, který nic nevyloučí, ale počítá se do odznaku.
 */
function parseStarValues(raw: string | null | undefined): number[] {
  return csvValues(raw)
    .map((value) => Number(value))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 5)
    .sort((a, b) => a - b);
}

/**
 * Datum z URL: jen kalendářně platné `YYYY-MM-DD`, cokoli jiného (`abc`,
 * `2026-13-45`, nezero-paddované `2026-8-1`) je NEPŘÍTOMNÝ parametr — dimenze
 * termínu se prostě nezapne, stejně jako u neznámého kontinentu nebo svátku.
 *
 * Není to kosmetika: od řezu 02 se nad `dateFrom`/`dateTo` počítá tolerance
 * (`shiftIsoDate`), takže nevalidované datum by v API skončilo `Invalid Date` →
 * `RangeError` → 500 na `/api/trips`, `/api/trips/map` i `/api/trips/routes`
 * (a na webu „API nedostupné" místo katalogu). Hlídá se tady, v JEDINÉM parseru
 * kontraktu, aby se past nezopakovala u dalšího konzumenta téhož stavu.
 */
function parseIsoDateParam(raw: string | null | undefined): string {
  const value = (raw ?? "").trim();
  return isIsoDate(value) ? value : "";
}

/**
 * Měsíc z URL: jen celé číslo 1–12, cokoli jiného (`0`, `13`, `5.5`, `kveten`, CSV tvar)
 * je NEPŘÍTOMNÝ parametr — táž norma jako u neznámého kontinentu nebo stylu.
 *
 * `Number()`, ne `parseInt`: prefixový parser by z `?bestMonth=5abc` udělal květen,
 * zatímco cena, datum i tolerance smetí odmítají.
 */
function parseMonthParam(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : null;
}

/**
 * Tolerance z URL: jen hodnota z nabídky, cokoli jiného (`99`, `abc`, `-3`,
 * `constructor`) degraduje na výchozí ±3 dny. Stejné pravidlo jako u ostatních
 * enumů kontraktu — sdílený odkaz s ručně upravenou hodnotou nesmí vrátit chybu.
 */
function parseDateTolerance(raw: string | null | undefined): DateTolerance {
  if (!raw) return DEFAULT_DATE_TOLERANCE;
  const n = Number(raw);
  return Number.isInteger(n) && isDateTolerance(n) ? n : DEFAULT_DATE_TOLERANCE;
}

// `hasOwnProperty`, ne `!== undefined`: `?duration=constructor` by jinak prošel jako
// „známý bucket" (dědí se z prototypu objektu), do filtru by se dostalo pásmo bez
// `min`/`max` — tedy filtr, který nic nevyloučí, ale počítá se do odznaku a nejde vypnout.
const isDurationBucketKey = (key: string): key is DurationBucketKey =>
  Object.prototype.hasOwnProperty.call(DURATION_BUCKET_BY_KEY, key);

/**
 * JEDINÝ parser jmenného prostoru filtrů — čte ho výpis `/zajezdy`, panel,
 * `/hotely` i `parseFilterParams` v API. Neznámé hodnoty se zahazují (filtr se
 * nezapne), aby sdílený nebo ručně upravený odkaz nikdy nevrátil prázdný
 * katalog bez vysvětlení.
 */
export function parseFilterSearchParams(params: URLSearchParams): TripFilterState {
  const get = (key: TripFilterParamKey) => params.get(key);

  /**
   * Vícehodnotová dimenze: OPAKOVANÝ výskyt parametru
   * (`?destination=vietnam&destination=japonsko`) znamená totéž co čárkový tvar
   * (`?destination=vietnam,japonsko`) — obojí se slévá do jednoho seznamu.
   *
   * `params.get()` vrací jen PRVNÍ výskyt, takže do téhle opravy sdílený odkaz
   * s opakovaným parametrem druhou zemi tiše zahodil: výpis vrátil jen tu první
   * a kanonizace adresu přepsala na její nabídkovou adresu, tedy bez jediné stopy po
   * tom, že se něco ztratilo. Aplikace opakovaný tvar sama negeneruje (serializátor píše
   * čárky), ale odkaz odjinud nesmí ukázat něco jiného, než co v sobě nese.
   *
   * Jednohodnotové dimenze (`continent`, `priceMin`, `dateFrom`, …) zůstávají u `get()`:
   * tam je opakování rozpor, ne součet, a první výskyt je jediná rozumná volba.
   */
  const getCsv = (key: TripFilterParamKey) => params.getAll(key).join(",");

  const durationRaw = getCsv("duration").trim();
  // Virtuální klíč platí jen jako CELÁ hodnota parametru — `?duration=day,1-10`
  // je bucketový výběr (tak to čtou obě dosavadní implementace).
  const virtualDuration = isVirtualDuration(durationRaw) ? durationRaw : null;

  return {
    destination: csvValues(getCsv("destination")),
    continent: pickKnown(CONTINENTS, get("continent")),
    region: pickKnown(DESTINATION_REGIONS, get("region")),
    // Typ je jednohodnotový (B2). Legacy odkaz s víc hodnotami (CSV i opakovaný
    // parametr) degraduje na PRVNÍ ZNÁMOU hodnotu po aplikaci aliasu: sdílený odkaz
    // nesmí skončit chybou ani prázdným katalogem.
    tripType:
      keepKnown(TRIP_TYPES, csvValues(getCsv("tripType")).map(normalizeTripTypeParam))[0] ?? null,
    hotels: get("hotels") === "true",
    tripStyle: keepKnown(TRIP_STYLES, csvValues(getCsv("tripStyle"))),
    difficultyLevel: keepKnown(DIFFICULTY_LEVELS, csvValues(getCsv("difficultyLevel"))),
    priceMin: parsePriceBound(get("priceMin"), PRICE_SLIDER.min),
    priceMax: parsePriceBound(get("priceMax"), PRICE_SLIDER.max),
    durationMin: parseDayBound(get("durationMin")),
    durationMax: parseDayBound(get("durationMax")),
    duration: virtualDuration ? [] : csvValues(durationRaw).filter(isDurationBucketKey),
    virtualDuration,
    mealPlan: keepKnown(HOTEL_MEAL_PLANS, csvValues(getCsv("mealPlan"))),
    hotelStars: parseStarValues(getCsv("hotelStars")),
    hotelFeature: keepKnown(HOTEL_FEATURES, csvValues(getCsv("hotelFeature"))),
    dateFrom: parseIsoDateParam(get("dateFrom")),
    dateTo: parseIsoDateParam(get("dateTo")),
    dateTolerance: parseDateTolerance(get("dateTolerance")),
    // Jednohodnotová dimenze → `get()`, ne `getAll()`: opakovaný parametr je rozpor,
    // ne součet (týž důvod jako u `continent`).
    bestMonth: parseMonthParam(get("bestMonth")),
    hasCzechGuide: get("hasCzechGuide") === "true",
    isFeatured: get("isFeatured") === "true",
    isLastMinute: get("isLastMinute") === "true",
    isForWomen: get("isForWomen") === "true",
    guide: (get("guide") ?? "").trim(),
    search: (get("search") ?? "").trim(),
  };
}

// ── Serializace ─────────────────────────────────────────────────────────────

/**
 * Zapíše stav do EXISTUJÍCÍHO query stringu: každý klíč jmenného prostoru se
 * nastaví nebo smaže, ostatní parametry (`sort`, `view`, UTM) přežijí.
 * Přesně to potřebuje panel — po „Použít" nesmí uživatel přijít o řazení.
 */
export function writeFilterSearchParams(
  target: URLSearchParams,
  state: TripFilterState,
): URLSearchParams {
  const set = (key: TripFilterParamKey, value: string) => {
    if (value) target.set(key, value);
    else target.delete(key);
  };
  const flag = (key: TripFilterParamKey, value: boolean) => set(key, value ? "true" : "");
  const num = (key: TripFilterParamKey, value: number | null) =>
    set(key, value !== null ? String(value) : "");

  set("destination", state.destination.join(","));
  set("continent", state.continent ?? "");
  set("region", state.region ?? "");
  set("tripType", state.tripType ?? "");
  flag("hotels", state.hotels);
  set("tripStyle", state.tripStyle.join(","));
  set("difficultyLevel", state.difficultyLevel.join(","));
  num("priceMin", state.priceMin);
  num("priceMax", state.priceMax);
  num("durationMin", state.durationMin);
  num("durationMax", state.durationMax);
  // Bucketový výběr vyhrává nad virtuální délkou — jakmile uživatel sáhne na
  // sekci Délka, zkratka z dlaždice HP se nahradí (a naopak se re-emituje,
  // dokud na ni nesáhne, aby ji nesmazala nesouvisející interakce v panelu).
  set(
    "duration",
    state.duration.length > 0 ? state.duration.join(",") : (state.virtualDuration ?? ""),
  );
  set("mealPlan", state.mealPlan.join(","));
  // Hvězdičky se řadí, aby dva stejné výběry v jiném pořadí klikání daly TÝŽ dotaz —
  // `filterStatesEqual` porovnává serializovaný tvar, takže neseřazené pole by
  // vyrobilo reload na adresu, která filtruje totéž.
  set("hotelStars", [...state.hotelStars].sort((a, b) => a - b).join(","));
  set("hotelFeature", state.hotelFeature.join(","));
  set("dateFrom", state.dateFrom);
  set("dateTo", state.dateTo);
  // Tolerance se do URL píše jen tehdy, když něco DĚLÁ. Dvě podmínky, každá z jiného
  // důvodu:
  //  1. výchozí hodnota ne — jinak by ji nesl každý sdílený odkaz a „Zrušit vše"
  //     (které maže celý jmenný prostor) by po sobě nechal parametr,
  //  2. bez zadaného termínu ne — je to modifikátor dimenze `dateFrom`/`dateTo`, takže
  //     nad prázdným rozsahem nic nefiltruje. Zapsaná by vyrobila odkaz, který mění
  //     výpis o nic, odznak „Filtry" hlásí 0 a panel kvůli němu natrvalo rozbaluje
  //     sekci „další filtry" — příjemci sdíleného odkazu navíc neříká vůbec nic.
  const toleranceApplies = !!state.dateFrom || !!state.dateTo;
  set(
    "dateTolerance",
    toleranceApplies && state.dateTolerance !== DEFAULT_DATE_TOLERANCE
      ? String(state.dateTolerance)
      : "",
  );
  num("bestMonth", state.bestMonth);
  flag("hasCzechGuide", state.hasCzechGuide);
  flag("isFeatured", state.isFeatured);
  flag("isLastMinute", state.isLastMinute);
  flag("isForWomen", state.isForWomen);
  set("guide", state.guide);
  set("search", state.search);
  for (const legacy of LEGACY_TRIP_FILTER_PARAM_KEYS) target.delete(legacy);
  return target;
}

/** Stav → čerstvý query string veřejné URL. */
export function toSearchParams(state: TripFilterState): URLSearchParams {
  return writeFilterSearchParams(new URLSearchParams(), state);
}

/**
 * Znamenají dva stavy TÝŽ dotaz? Odpověď „ano" znamená, že navigace NEMÁ proběhnout.
 *
 * Ne každá změna stavu panelu je změna dotazu: tolerance termínu je modifikátor dimenze
 * `dateFrom`/`dateTo`, takže ji `writeFilterSearchParams` bez zadaného termínu do URL
 * vůbec nepustí. Desktopový auto-apply (jakákoli změna `filters` → reload) z toho dělal
 * reload na prakticky totožnou adresu, který zahodil jak zvolenou toleranci (z URL se
 * nemá jak vrátit), tak rozbalenou sekci „další filtry" — klik v panelu se sám vrátil.
 *
 * Porovnává se přes TÝŽ serializátor v obou směrech, takže na pořadí parametrů ani na
 * normalizovatelných tvarech (legacy `?tripType=small-group`, kraj Kč slideru, neznámý
 * bucket) nezáleží — rovnost tady znamená „stejný filtr", ne „stejný řetězec".
 *
 * Vstupem jsou STAVY, ne query stringy: od řezu 02 nese na nabídkové adrese
 * (`zajezdy/vietnam`) část zúžení CESTA, takže porovnání proti parametrům by hlásilo
 * „změnilo se" pokaždé a guard by přestal fungovat právě tam, kde je potřeba.
 */
export function filterStatesEqual(a: TripFilterState, b: TripFilterState): boolean {
  return toSearchParams(a).toString() === toSearchParams(b).toString();
}

/**
 * „Zrušit vše": smaže CELÝ jmenný prostor filtrů včetně legacy parametrů.
 * Nutně i ty, pro které panel nemá ovládací prvek (`region`, `continent`) —
 * jinak uživatel kouká na zúžený katalog s prázdným panelem. Stránkování
 * (`page`) ani prezentační parametry (`sort`, `view`) sem nepatří.
 */
export function clearFilterSearchParams(target: URLSearchParams): URLSearchParams {
  for (const key of TRIP_FILTER_PARAM_KEYS) target.delete(key);
  for (const legacy of LEGACY_TRIP_FILTER_PARAM_KEYS) target.delete(legacy);
  return target;
}

/**
 * Meze délky v dnech pro API. Virtuální klíč (`day`/`multi`) je zkratka, kterou
 * API nezná — překládá se PRÁVĚ TADY, takže výpis i samotný filtr v API čtou
 * stejné číslo.
 */
export function resolveDurationBounds(
  state: Pick<TripFilterState, "virtualDuration" | "durationMin" | "durationMax">,
): { min: number | null; max: number | null } {
  if (state.virtualDuration) {
    const range = VIRTUAL_DURATIONS[state.virtualDuration];
    return { min: range.min, max: range.max };
  }
  return { min: state.durationMin, max: state.durationMax };
}

/**
 * Stav → dotaz na `/api/trips` (a `/map`, `/routes`). Shodný se `toSearchParams`
 * až na překlad virtuální délky na `durationMin/durationMax`. Volající si k němu
 * dosadí prezentační parametry (`sortBy`, `page`, `limit`, `fields`).
 */
export function toApiParams(state: TripFilterState): URLSearchParams {
  const bounds = resolveDurationBounds(state);
  return toSearchParams({
    ...state,
    virtualDuration: null,
    durationMin: bounds.min,
    durationMax: bounds.max,
  });
}

/**
 * Přesný tvar dotazu, ze kterého renderuje výloha `/hotely` a rail „další podobné
 * hotely" na detailu hotelu — a který proto musí purgovat `hotelListApiUrls`
 * (purge je na PŘESNOU URL včetně pořadí parametrů). Dřív se skládal třikrát
 * ručně s komentářem „keep in sync"; teď je to jeden builder, takže se rozejít
 * nemůže. Hotel = pobytový na vyžádání (`?hotels=true`), ne `tripType=resort`.
 */
export function hotelListApiQuery(destinationSlug?: string): URLSearchParams {
  const query = toApiParams({
    ...emptyTripFilterState(),
    hotels: true,
    destination: destinationSlug ? [destinationSlug] : [],
  });
  query.set("fields", "slim");
  query.set("limit", "100");
  return query;
}

/**
 * Přesný tvar dotazu, ze kterého detail zájezdu skládá rail „Podobné zájezdy" — a který
 * proto musí purgovat `similarTripsApiUrls` (purge je na PŘESNOU URL VČETNĚ POŘADÍ
 * parametrů).
 *
 * Existuje ze stejného důvodu jako `hotelListApiQuery` a `tripListingApiQuery`: dokud si
 * ho web skládal template literálem a purge o něm nevěděl vůbec, znovu se rail po
 * publikaci zájezdu složil ze staré (≤120 s) odpovědi — tedy čerstvě purgnuté HTML
 * detailu vykreslilo zastaralý seznam.
 *
 * Kandidáti se berou z celého KONTINENTU destinace, ne z její lokality (místní výběr
 * dával tak tři karty a karusel neměl co posouvat); bez známého kontinentu se sáhne do
 * celého katalogu. `fields=slim` drží payload malý — karta z plné projekce nic navíc
 * nečte. Řazení „nejdřív stejná destinace" dělá stabilní sort až na webu, takže tady
 * žádný `sortBy` není.
 */
export function similarTripsApiQuery(continent?: string | null): URLSearchParams {
  const query = new URLSearchParams();
  query.set("fields", "slim");
  if (continent) query.set("continent", continent);
  return query;
}

// ── Dotaz výpisu katalogu ───────────────────────────────────────────────────

/** Kolik karet je na jedné stránce gridu. Součást TVARU URL, ne jen zobrazení. */
export const TRIP_LISTING_PAGE_SIZE = 24;

/**
 * Řazení z UI (`?sort=`) → parametry API. Neznámá hodnota (i výchozí „recommended")
 * nechává řazení na API, tzn. doporučené pořadí.
 */
const LISTING_SORT_API: Readonly<Record<string, { sortBy: string; sortOrder: "asc" | "desc" }>> = {
  "price-asc": { sortBy: "price", sortOrder: "asc" },
  "price-desc": { sortBy: "price", sortOrder: "desc" },
  "duration-asc": { sortBy: "duration", sortOrder: "asc" },
  "duration-desc": { sortBy: "duration", sortOrder: "desc" },
};

/**
 * Je to hodnota `?sort=`, kterou nabídka řazení opravdu zná? ALLOWLIST, a jediný —
 * `?sort=` totiž do řezu 05 nikde neprocházel žádnou kontrolou (výpis ho četl
 * `searchParams.get("sort")` a posílal rovnou sem).
 *
 * `hasOwnProperty`, ne holý index: `?sort=constructor` (též `toString`, `valueOf`,
 * `__proto__`) by z objektového literálu vytáhl ZDĚDĚNOU hodnotu. Ta je pravdivostní,
 * takže `hasExplicitListingSort` by vrátilo `true` a API by pod „Doporučené" tiše vyplo
 * garanci tří typů, blízkost termínu i skupinu „nejdřív s odletem"; `tripListingApiQuery`
 * by zároveň poslal `&sortBy=undefined&sortOrder=undefined`. Táž past, jakou tenhle
 * soubor řeší u `?duration=` (`isDurationBucketKey`) a `?tripType=`
 * (`normalizeTripTypeParam`) — proto stejným způsobem.
 *
 * Výchozí „Doporučené" tu ZÁMĚRNĚ není: je to jediná volba nabídky, která se na dotaz
 * nepřekládá (řazení nechává na API), takže z pohledu tohohle predikátu se od neznámé
 * hodnoty neliší.
 */
export function isListingSortKey(sort: string | null | undefined): boolean {
  return !!sort && Object.prototype.hasOwnProperty.call(LISTING_SORT_API, sort);
}

/**
 * Vyslovil zákazník řadicí kritérium sám? (`?sort=price-asc` ano, „Doporučené" ne.)
 *
 * API tutéž otázku čte jako `Boolean(query("sortBy"))` a staví na ní DVĚ pravidla
 * výchozího pořadí — blízkost termínu (E4) a skupinu „nejdřív s odletem". Obojí se pod
 * explicitním řazením vypíná, protože select nad gridem slibuje „od nejlevnějších"
 * a výpis nesmí tvrdit něco jiného. Web tedy musí odpovědět STEJNĚ, jinak přeskládá
 * stránku, kterou API seřadilo čistě podle ceny — a odpověď je odsud, ne z intuice:
 * `sortBy` do dotazu vkládá jedině `LISTING_SORT_API` o kus výš.
 */
export function hasExplicitListingSort(sort: string | null | undefined): boolean {
  return isListingSortKey(sort);
}

/** Pohled výpisu — stránkování a slim projekce platí jen pro grid. */
export type TripListingView = "grid" | "map" | "routes";

export interface TripListingQueryOptions {
  page?: number;
  /** Hodnota `?sort=` z UI; neznámá nebo chybějící nechává řazení na API. */
  sort?: string | null;
  view?: TripListingView;
}

/**
 * PŘESNÝ tvar dotazu, ze kterého renderuje výpis katalogu — `/zajezdy` i všechny
 * nabídkové adresy (`zajezdy/<destinace>`, `zajezdy/<typ>`, `zajezdy/<destinace>/<typ>`).
 *
 * Existuje ze stejného důvodu jako `hotelListApiQuery` o kus výš: **purge je na PŘESNOU
 * URL včetně pořadí parametrů**, takže kdyby si web a mazání mezipaměti skládaly dotaz
 * každý po svém, rozejdou se — a projeví se to jenom tak, že uložený zájezd naskočí na
 * nabídkové adrese až po vypršení `s-maxage`. Přesně to se stalo v E2E řezu 02: purge
 * mířil na holé `?destination=japonsko`, kdežto stránka četla
 * `?destination=japonsko&page=1&limit=24&fields=slim`, takže se změna projevila za 89–110 s
 * místo do 15 s. Proto tenhle builder, a proto ho volá i `tripListingApiUrls` v purge.
 *
 * Pořadí je součást kontraktu: filtry (v pořadí `TRIP_FILTER_PARAM_KEYS`) → řazení →
 * stránkování a projekce.
 */
export function tripListingApiQuery(
  narrow: Partial<TripFilterState> = {},
  options: TripListingQueryOptions = {},
): URLSearchParams {
  const query = toApiParams({ ...emptyTripFilterState(), ...narrow });

  // `isListingSortKey`, ne holý index — jinak `?sort=constructor` vloží do dotazu
  // `sortBy=undefined` (viz docblock predikátu).
  if (isListingSortKey(options.sort)) {
    const sortApi = LISTING_SORT_API[options.sort as string];
    query.set("sortBy", sortApi.sortBy);
    query.set("sortOrder", sortApi.sortOrder);
  }

  // `/api/trips/map` a `/api/trips/routes` mají vlastní projekce a nestránkují.
  if ((options.view ?? "grid") === "grid") {
    query.set("page", String(options.page ?? 1));
    query.set("limit", String(TRIP_LISTING_PAGE_SIZE));
    query.set("fields", "slim");
  }
  return query;
}

// ── Odznak aktivních filtrů ─────────────────────────────────────────────────

/**
 * Dimenze, které se počítají do odznaku „Další filtry (N)" = PRÁVĚ TY, které má postranní
 * panel. Odznak visí na tlačítku, které panel otevírá, takže číslo musí odpovídat tomu,
 * co za tím tlačítkem jde vypnout — jinak uživatel hledá v panelu filtr, který je jinde.
 *
 * Destinace, typ a termín se sem v řezu 3 nastěhovaly (panel tehdy pohltil celé ovládání
 * katalogu) a teď zase odcházejí: mají vlastní pole ve VYHLEDÁVACÍM BLOKU nad výpisem,
 * který je vidět na každé šířce obrazovky. Zvolená hodnota je tedy pořád na očích —
 * počítat ji do odznaku panelu by znamenalo hlásit „Další filtry (2)" nad filtry, které
 * zákazník vidí o kus výš vypsané slovy. S nimi odpadl i důvod měřit odznak proti stavu
 * z CESTY: zbylé dimenze cesta nikdy nenese (`zajezdy/vietnam/s-pruvodcem` je destinace
 * a typ, nic jiného), takže `countActiveFilters` se vrátil k holému stavu.
 *
 * Vícehodnotové dimenze se počítají po hodnotách (zaškrtnutí druhého délkového pásma
 * zvedne číslo o jedna), cena po JEDNÉ dimenzi (dvě táhla jednoho posuvníku).
 */
export const COUNTED_TRIP_FILTER_KEYS = [
  "tripStyle",
  "priceMin",
  "priceMax",
  "duration",
  "virtualDuration",
  // Dimenze pobytových — panel je vykresluje jen u `tripType=resort`, ale počítat se
  // musí vždy: odznak odpovídá na otázku „mám tu něco zapnuté", a zapnutá strava
  // z URL zůstává zapnutá i po přepnutí typu (zrušit ji jde „Zrušit vše").
  "mealPlan",
  "hotelStars",
  "hotelFeature",
  // Řez 09: obě nové dimenze mají v panelu ovládací prvek (sekce „Kdy vyrazit"
  // a zaškrtávátko na konci sekce Styl / Zaměření hotelu), takže do odznaku PATŘÍ —
  // odchylka proti `continent`/`isFeatured`, které prvek nemají a mají jen chip.
  "bestMonth",
  "isForWomen",
] as const;

/**
 * Dimenze MIMO odznak. Většina proto, že pro ně panel nemá ovládací prvek, takže
 * by uživatel viděl číslo, které nemá kde vypnout: `continent` je vnější afordance
 * (mega menu, patička, `/o-nas`) se zrušitelným chipem nad výpisem — rozhodnutí z
 * řezu 11, `region` je jeho precedens. `hotels` je výloha `/hotely`, zbytek jsou
 * parametry pro API a interní odkazy.
 *
 * `isFeatured` je od řezu 13 TVRDÝ filtr, ale do odznaku se pořád nepočítá — je to
 * PŘESNĚ týž případ jako `continent`: vnější afordance (odkaz „Zobrazit všechny" v mega
 * menu), pro kterou panel ovládací prvek nemá. Do počítadla nepatří, protože by uživatel
 * viděl číslo, které nemá kde vypnout; zrušit ho jde zrušitelným CHIPEM nad výpisem
 * (`/zajezdy`), stejným jako u kontinentu. Bez toho chipu byla `/zajezdy?isFeatured=true`
 * slepá ulička: 18 z ~60 zájezdů, „Filtry" bez odznaku, prázdný panel a ani „Zrušit vše"
 * (to se vykresluje jen při nenulovém odznaku). Nový tvrdý filtr bez ovládacího prvku
 * v panelu proto MUSÍ dostat chip — jinak z něj není cesta zpět.
 *
 * `destination`, `tripType`, `dateFrom` a `dateTo` jsou ČTVRTÝ případ a jediný, který sem
 * patří z opačného důvodu než ostatní: ovládací prvek MAJÍ, jenže ne v panelu — stojí ve
 * vyhledávacím bloku nad výpisem, viditelném na každé šířce. Odznak panelu o nich proto
 * mlčí a chip nad výpisem taky ne (byl by druhá kopie vyplněného pole). Zrušit je jde
 * přímo v poli: křížek u odznaku destinace, volba „Vše" v typu, „Zrušit" v kalendáři.
 * „Je vůbec co zrušit" se pořád neptá na tohle číslo, ale na `hasAnyFilter`.
 *
 * `durationMin`/`durationMax` jsou třetí případ téhož pravidla: panel ovládá délku PÁSMY
 * (`?duration=`) a holé meze jsou překlad virtuální zkratky (`?duration=day|multi`) do
 * dotazu na API. V adrese výpisu se objeví leda ručně nebo z cizího odkazu — a od téhle
 * opravy mají zrušitelný chip (`buildNarrowingChips`), přesně jak to o dva odstavce výš
 * vyžaduje pravidlo o tvrdém filtru bez ovládacího prvku.
 *
 * `dateTolerance` je tu z JINÉHO důvodu: není to samostatná dimenze, ale modifikátor
 * termínu, a jeho výchozí hodnota je nenulová — počítaný by rozsvítil „Filtry (1)" i nad
 * prázdným panelem, případně by se musel počítat podmíněně a uživatel by číslo nedokázal
 * spárovat s žádným ovládacím prvkem. Nevracet ji do počítadla.
 */
export const UNCOUNTED_TRIP_FILTER_KEYS = [
  "destination",
  "tripType",
  "dateFrom",
  "dateTo",
  "continent",
  "region",
  "hotels",
  "difficultyLevel",
  "durationMin",
  "durationMax",
  "dateTolerance",
  "hasCzechGuide",
  "isFeatured",
  "isLastMinute",
  "guide",
  "search",
] as const;

/**
 * JEDNA definice odznaku „Další filtry (N)" — sdílí ji SSR výpis i panel, takže nemohou
 * ukázat jiné číslo.
 *
 * Počítá se HOLÝ stav, protože počítané dimenze nemůže nést cesta adresy: do cesty jde
 * jedině destinace a typ (`zajezdy/vietnam/s-pruvodcem`) a ty se od přesunu do
 * vyhledávacího bloku nepočítají vůbec. Do řezu 3 to tak bylo taky; mezitím měla funkce
 * druhý parametr `pathState`, aby `zajezdy/vietnam` nehlásilo „Filtry (1)" nad filtrem,
 * který si zákazník nezvolil. Ten důvod zanikl s dimenzemi, kterých se týkal — kdyby se
 * do odznaku někdy vrátila dimenze zapsatelná do cesty, musí se vrátit i to porovnání.
 */
export function countActiveFilters(state: TripFilterState): number {
  return (
    // Cena je JEDEN posuvník se dvěma táhly, tedy jedna sekce panelu a jedno číslo.
    (state.priceMin !== null || state.priceMax !== null ? 1 : 0) +
    state.duration.length +
    (state.virtualDuration ? 1 : 0) +
    state.tripStyle.length +
    state.mealPlan.length +
    state.hotelStars.length +
    state.hotelFeature.length +
    (state.bestMonth !== null ? 1 : 0) +
    (state.isForWomen ? 1 : 0)
  );
}

/**
 * Je zapnutý JAKÝKOLI filtr jmenného prostoru? Odpověď „ano" = má smysl nabídnout
 * „Zrušit vše" (které maže celý jmenný prostor VČETNĚ zúžení neseného cestou).
 *
 * NENÍ to `countActiveFilters(state) > 0`: odznak „Filtry (N)" mluví o tom, co si
 * zákazník zvolil NAD RÁMEC CESTY, a nepočítá vnější afordance bez ovládacího prvku
 * (`?continent=`, `?isFeatured=`). Kdyby na odznaku viselo i „Zrušit vše", zmizela by
 * jediná hromadná cesta zpět právě na zúžených adresách (`zajezdy/vietnam` má odznak 0,
 * ale zrušit je co — zúžení nese cesta).
 *
 * Ptá se SERIALIZÁTOREM, ne výčtem klíčů: „zapnutý filtr" = ten, který je vidět
 * v adrese, takže se pravidlo nemůže rozejít s tím, co „Zrušit vše" opravdu smaže.
 * Jediná dimenze, která tím propadne, je `dateTolerance` — modifikátor, který se bez
 * zadaného termínu do URL vůbec nepíše, a samotný tedy není co rušit (guard to tvrdí
 * výslovně).
 */
export function hasAnyFilter(state: TripFilterState): boolean {
  return toSearchParams(state).toString() !== "";
}

/**
 * Klíče, které jsou pouhé ZÚŽENÍ ROZSAHU katalogu, ne volba zákazníka v panelu.
 *
 * PODMNOŽINA toho, co v API obsluhuje `applyDestinationDerivedFilters` — ta aplikuje
 * všechny filtry ODVOZENÉ Z DESTINACÍ zájezdu, a rozsahové jsou z nich jen tyhle dvě.
 * `bestMonth` (měsíc nejlepšího počasí) je destinační taky, ale je to PREFERENCE
 * zákazníka z panelu, ne zúžení rozsahu — kdyby propadl sem, přestal by nad ním
 * `hasPreferenceBeyondScope` umlčovat garanci tří typů.
 *
 * `region` sem NEPATŘÍ: `applyDestinationDerivedFilters` ho neřeší, je to interní
 * odkazová dimenze a být tu konzervativní nic nestojí.
 */
const SCOPE_FILTER_PARAM_KEYS = [
  "destination",
  "continent",
] as const satisfies ReadonlyArray<TripFilterParamKey>;

/**
 * Vyslovil zákazník JAKOUKOLI preferenci nad rámec ROZSAHU? (vize struktura-webu D9)
 *
 * Odpověď „ne" = výpis je ve výchozím stavu, a tedy smí doplnit chybějící typ zájezdu
 * mezi první karty (`applyTypeCoverage`). Odpověď „ano" = garance MLČÍ, protože by
 * jinak do zvoleného zúžení vstřelila zájezd, o který zákazník neprosil (hotelová
 * výloha `?hotels=true`, kurátorský karusel `?isFeatured=true`, svátkový filtr…).
 *
 * Ptá se SERIALIZÁTOREM se smazaným rozsahem, ne výčtem klíčů — stejná disciplína jako
 * `hasAnyFilter`, takže nový filtr garanci umlčí sám od sebe, aniž by na něj někdo
 * musel myslet.
 *
 * Rozsah = `destination` + `continent`, nic víc. D9 mlčí „bez zvoleného typu, řazení
 * a termínu" a výslovně nechává garanci platit na nabídkové adrese destinace; kontinent
 * je zúžení téže povahy — `/zajezdy?continent=asia` je vnější afordance mega menu
 * a patičky („Vše z Asie"), ne volba v panelu (`UNCOUNTED_TRIP_FILTER_KEYS` ho tak
 * i popisují). Kdyby kontinent garanci umlčel, byla by celá kontinentová větev menu
 * jediným místem, kde zákazník bez vlastní volby vidí jen jeden typ.
 *
 * Jediná dimenze, která tím propadne, je `dateTolerance` — modifikátor, který se bez
 * zadaného termínu do URL vůbec nepíše (týž případ a týž důvod jako u `hasAnyFilter`).
 */
export function hasPreferenceBeyondScope(state: TripFilterState): boolean {
  const query = toSearchParams(state);
  for (const key of SCOPE_FILTER_PARAM_KEYS) query.delete(key);
  return query.toString() !== "";
}

/**
 * Měkké filtry (skóre → `_isMatch`, nevyhovující zájezdy zůstávají pod čarou)
 * proti tvrdým (striktní vyloučení). Čte to API při filtrování i výpis, když
 * se rozhoduje, jestli vykreslit nadpis „Zobrazujeme N…" a čáru.
 *
 * `isFeatured` sem ZÁMĚRNĚ nepatří (byl tu do řezu 13): obě jeho afordance ho čtou
 * jako výběr, ne jako preferenci — „Zobrazit všechny" v mega menu vede na
 * `/zajezdy?isFeatured=true` a kurátorský karusel homepage z něj kreslí sekci. Jako
 * měkký filtr obojí lhalo: menu ukázalo CELÝ katalog s oblíbenými nahoře a homepage
 * si musela dofiltrovat sama nad úmyslně nafouknutým `limit`. (Do řezu 08 navíc nic
 * nepřidával — výchozí řazení featured nahoru zvedalo tak jako tak; od té doby o pořadí
 * rozhoduje ruční žebříček a příznak je čistě filtr a odznak.)
 */
export function hasSoftFilters(state: TripFilterState): boolean {
  return (
    state.tripStyle.length > 0 ||
    state.difficultyLevel.length > 0 ||
    state.hasCzechGuide ||
    state.isLastMinute
  );
}

// ── Drobné pomůcky pro UI ───────────────────────────────────────────────────

/** Přepnutí hodnoty ve vícehodnotové dimenzi (chipy nad výpisem, checkboxy v panelu). */
export function toggleFilterValue<T extends string | number>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/** Stav → pozice obou táhel Kč slideru (`null` = kraj). */
export function priceSliderRange(
  state: Pick<TripFilterState, "priceMin" | "priceMax">,
): [number, number] {
  return [state.priceMin ?? PRICE_SLIDER.min, state.priceMax ?? PRICE_SLIDER.max];
}

/** Pozice táhel → stav (kraj = parametr chybí). */
export function priceBoundsFromSlider(
  range: readonly [number, number],
): Pick<TripFilterState, "priceMin" | "priceMax"> {
  const [min, max] = range;
  return {
    priceMin: min > PRICE_SLIDER.min ? min : null,
    priceMax: max < PRICE_SLIDER.max ? max : null,
  };
}
